import React from 'react';
import { omit } from 'lodash';
import moment from 'moment';
import styled from 'styled-components';

import { useInstructor } from 'contexts/instructorRole';
import { useSnackbar } from 'contexts/snackbar';
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Box,
    Button,
    Checkbox,
    CompletionButton,
    DatePicker,
    Divider,
    Dropdown,
    FormLabel,
    Icon,
    IconButton,
    MenuItem,
    Radio,
    RadioGroup,
    ResourceChip,
    RichTextArea,
    Select,
    Stack,
    Tag,
    TaskTile,
    Text,
    TextInput,
} from 'shell/components';
import { TASK_WORKLOAD_OPTIONS } from 'utils/constants';
import { downloadFileFromURL, getAbsoluteURL, getS3FileURL } from 'utils/helper';

const getSubtitleByState = (templateState, template) => {
    switch (templateState) {
        case 'draft':
            return `Draft created on ${moment(template.created_on).format('MMMM D[,] YYYY')}`;
        case 'published':
            return `Published on ${moment(template.created_on).format('MMMM D[,] YYYY')}`;
        case 'scheduled':
            return `Scheduled to Publish on ${moment(template.published_on).format('MMMM D[,] YYYY')}`;
    }
};

const TemplateTaskView = ({
    templateState,
    template,
    files,
    sharedUrls,
    changes,
    groups,
    spawnedTasks,
    updateTemplate,
    onAddResources,
    onDeleteResource,
    onAddSubtask,
    onClickSubtask,
    onDeleteSubtask,
}) => {
    const { milestones } = useInstructor();
    const { message } = useSnackbar();

    template = { ...template, ...omit(changes, ['subtasks']) };

    const milestoneOptions = [
        { key: 'none', value: 'None' },
        ...milestones
            .filter(({ active }) => active)
            .map((milestone) => ({ ...milestone, key: milestone.id, value: milestone.title })),
    ];
    const linkedMilestone = milestones.find((milestone) => milestone.id === template.milestone_id);

    const workloadOptions = TASK_WORKLOAD_OPTIONS.map((option) => ({
        key: option.value,
        value: <Tag sx={{ width: '100%' }} type={'workload'} value={option.value} />,
    }));

    const fileResources = Object.values(files)
        .filter((file) => file.active && file.template_id === template.id)
        .map((file) => ({ ...file, origin: file.type, type: 'file' }));
    const sharedUrlResources = Object.values(sharedUrls)
        .filter((sharedUrl) => sharedUrl.active && sharedUrl.template_id === template.id)
        .map((sharedUrl) => ({ ...sharedUrl, origin: sharedUrl.type, type: 'link' }));
    const resources = [...fileResources, ...sharedUrlResources].sort((a, b) => a.created_on - b.created_on);

    template.subtasks = [
        ...(template.subtasks || []),
        ...(changes.subtasks || []).filter((subtask) => subtask.action === 'add'),
    ]
        .map((subtask) => {
            return (changes.subtasks || []).find(({ id }) => id === subtask.id) || subtask;
        })
        .filter((subtask) => subtask.action !== 'delete');

    const handleSelectSubtaskDropdown = (action, subtaskId) => {
        if (action === 'edit') {
            onClickSubtask(subtaskId);
        } else if (action === 'delete') {
            onDeleteSubtask(subtaskId);
        }
    };

    const renderMilestone = () => {
        return (
            <>
                <CustomFormLabel htmlFor={'template-milestone'}>
                    <Text variant={'p2'} weight={'semibold'}>
                        Milestone
                    </Text>
                </CustomFormLabel>
                <CustomSelect
                    data-cy={'milestone-select'}
                    inputProps={{ id: 'template-milestone' }}
                    value={linkedMilestone?.id || 'none'}
                    onChange={(e) => updateTemplate('milestone_id', e.target.value === 'none' ? '' : e.target.value)}
                >
                    {milestoneOptions.map(({ key, value }) => (
                        <MenuItem key={key} value={key}>
                            {value}
                        </MenuItem>
                    ))}
                </CustomSelect>
            </>
        );
    };

    const renderWorkload = () => {
        if (!template.workload) return null;
        return (
            <>
                <CustomFormLabel htmlFor={'template-workload'}>
                    <Text variant={'p2'} weight={'semibold'}>
                        Workload
                    </Text>
                </CustomFormLabel>
                <CustomSelect
                    data-cy={'workload-select'}
                    inputProps={{ id: 'template-workload' }}
                    renderValue={(selected) => {
                        return workloadOptions.find(({ key }) => key === selected).value;
                    }}
                    value={template.workload}
                    onChange={(e) => updateTemplate('workload', e.target.value)}
                >
                    {workloadOptions.map(({ key, value }) => (
                        <MenuItem key={key} value={key}>
                            {value}
                        </MenuItem>
                    ))}
                </CustomSelect>
            </>
        );
    };

    const renderDueDate = () => {
        return (
            <>
                <CustomFormLabel htmlFor={'template-datepicker'}>
                    <Text variant={'p2'} weight={'semibold'}>
                        Due Date
                    </Text>
                </CustomFormLabel>
                <CustomDatePicker
                    id={'template-datepicker'}
                    value={(template.end_on && moment(template.end_on)) || moment()}
                    onChange={(timestamp) => updateTemplate('end_on', timestamp.valueOf())}
                />
            </>
        );
    };

    const renderDescription = () => {
        return (
            <>
                <FormLabel sx={{ mt: '10px' }} htmlFor={'template-description'}>
                    <Text variant={'p2'} weight={'semibold'}>
                        Details
                    </Text>
                </FormLabel>
                <CustomRichTextArea
                    id={'template-description'}
                    placeholder={'Enter Task Details'}
                    value={template.description || ''}
                    variant={'dynamic'}
                    onChange={(html) => updateTemplate('description', html)}
                />
            </>
        );
    };

    const renderResources = () => {
        const handleSelect = ({ key }, resource) => {
            switch (key) {
                case 'download':
                    downloadFileFromURL(getS3FileURL(resource.data), resource.data.name);
                    break;
                case 'visit':
                    downloadFileFromURL(getAbsoluteURL(resource.url));
                    break;
                case 'copy':
                    navigator.clipboard.writeText(getAbsoluteURL(resource.url));
                    message.success('Link copied to clipboard.');
                    break;
                case 'delete':
                    onDeleteResource(resource.type, resource.id);
                    break;
                default:
                    break;
            }
        };

        return (
            <>
                <FormLabel sx={{ mt: '10px' }} htmlFor={'template-resources'}>
                    <Text variant={'p2'} weight={'semibold'}>
                        Resources
                    </Text>
                </FormLabel>
                <Stack sx={{ minWidth: 0 }} alignItems={'flex-start'} spacing={2}>
                    {resources.length ? (
                        <Stack sx={{ flexWrap: 'wrap', maxWidth: '100%' }} spacing={2}>
                            {resources.map((resource) => {
                                return (
                                    <ResourceChip
                                        key={resource.id}
                                        options={[
                                            ...(resource.type === 'file'
                                                ? [
                                                      { key: 'download', value: 'Download' },
                                                      { key: 'delete', value: 'Delete' },
                                                  ]
                                                : [
                                                      { key: 'visit', value: 'Visit Link' },
                                                      { key: 'copy', value: 'Copy Link' },
                                                      { key: 'delete', value: 'Delete' },
                                                  ]),
                                        ]}
                                        resource={resource}
                                        size={'small'}
                                        type={resource.type}
                                        onSelect={(option) => handleSelect(option, resource)}
                                    />
                                );
                            })}
                        </Stack>
                    ) : null}
                    <Button
                        data-cy={'add-resources-button'}
                        startIcon={<Icon kind={'add'} />}
                        variant={'tertiary'}
                        onClick={onAddResources}
                    >
                        Add Resources
                    </Button>
                </Stack>
            </>
        );
    };

    return (
        <>
            <TitleTextInput
                data-cy={'task-template-title'}
                placeholder={'Try starting the title with a verb...'}
                value={template.title || ''}
                onChange={(e) => updateTemplate('title', e.target.value)}
            />
            {!template.isLocal ? (
                <Text color={'light'} variant={'p2'}>
                    {getSubtitleByState(templateState, template)}
                </Text>
            ) : null}
            <TaskInfoBox>
                {renderMilestone()}
                {renderWorkload()}
                {renderDueDate()}
                {renderDescription()}
                {renderResources()}
            </TaskInfoBox>
            <Divider />
            <Stack direction={'row'}>
                <Text sx={{ width: '5.625rem' }} variant={'p2'} weight={'semibold'}>
                    Visibility
                </Text>
                <Stack spacing={2}>
                    <RadioGroup
                        value={template.visible_to_all ? 'all' : 'custom'}
                        onChange={(e) => updateTemplate('visible_to_all', e.target.value === 'all')}
                    >
                        <Radio label={'All Teams'} value={'all'} />
                        <Radio label={'Custom'} value={'custom'} />
                    </RadioGroup>
                    {!template.visible_to_all ? (
                        <Accordion defaultExpanded>
                            <AccordionSummary>
                                <Text variant={'p2'}>Teams Selected ({template.visible_to?.length})</Text>
                            </AccordionSummary>
                            <AccordionDetails>
                                <Stack>
                                    {groups
                                        .sort((a, b) => (a.name.toLowerCase() > b.name.toLowerCase() ? 1 : -1))
                                        .map((group) => (
                                            <Checkbox
                                                key={group.id}
                                                checked={template.visible_to?.includes(group.id)}
                                                label={group.name}
                                                onChange={() => updateTemplate('visible_to', group.id)}
                                            />
                                        ))}
                                </Stack>
                            </AccordionDetails>
                        </Accordion>
                    ) : null}
                </Stack>
            </Stack>
            <Divider />
            <Stack spacing={1}>
                <Text variant={'p2'} weight={'semibold'}>
                    Subtasks ({template.subtasks.length || 0})
                </Text>
                {template.subtasks.map((subtask) => (
                    <TaskTile
                        key={subtask.id}
                        endComponent={
                            <Dropdown
                                id={`template-subtask-${subtask.id}-dropdown`}
                                options={[
                                    { key: 'edit', value: 'Edit' },
                                    { key: 'delete', value: 'Delete' },
                                ]}
                                rootComponent={
                                    <IconButton
                                        aria-label={'more options'}
                                        kind={'moreHorizontal'}
                                        size={'small'}
                                        variant={'square'}
                                        onClick={(e) => e.stopPropagation()}
                                    />
                                }
                                onSelect={({ key }) => handleSelectSubtaskDropdown(key, subtask.id)}
                            />
                        }
                        task={subtask}
                        onClick={() => onClickSubtask(subtask.id)}
                    />
                ))}
                <Button
                    sx={{ alignSelf: 'flex-start' }}
                    startIcon={<Icon kind={'add'} />}
                    variant={'tertiary'}
                    onClick={onAddSubtask}
                >
                    Add Subtask
                </Button>
            </Stack>
            {templateState === 'published' && (
                <>
                    <Divider />
                    <Stack spacing={4}>
                        <Text variant={'p2'} weight={'semibold'}>
                            Completion (
                            {
                                Object.values(spawnedTasks).filter(
                                    (task) => task?.template_id === template.id && task?.status === 'completed'
                                ).length
                            }
                            /{template.visible_to_all ? template.task_ids.length : template.visible_to.length})
                        </Text>
                        <Stack spacing={2}>
                            {groups
                                .filter((group) => template.visible_to_all || template.visible_to.includes(group.id))
                                .sort((a, b) => (a.name.toLowerCase() > b.name.toLowerCase() ? 1 : -1))
                                .map((group) => (
                                    <Stack
                                        key={group.id}
                                        alignItems={'center'}
                                        direction={'row'}
                                        justifyContent={'space-between'}
                                        spacing={2}
                                    >
                                        <Text noWrap variant={'p2'}>
                                            {group.name}
                                        </Text>
                                        <CompletionButton
                                            checked={Object.values(spawnedTasks).find(
                                                (task) =>
                                                    task?.group_id === group.id &&
                                                    task?.template_id === template.id &&
                                                    task?.status === 'completed'
                                            )}
                                        />
                                    </Stack>
                                ))}
                        </Stack>
                    </Stack>
                </>
            )}
        </>
    );
};

const TitleTextInput = styled(TextInput)(({ theme }) => {
    return {
        margin: theme.spacing(0, -3),
        width: `calc(100% + ${theme.spacing(6)})`,
        '&& input': {
            fontSize: `${theme.typography.sh1.fontSize.large}rem`,
            fontWeight: theme.typography.sh1.fontWeight.semibold,
            letterSpacing: `${theme.typography.sh1.letterSpacing.large}rem`,
            lineHeight: `${theme.typography.getLineHeight('sh1', 'large')}rem`,
        },
        '& fieldset': {
            border: 'none',
        },
    };
});

const TaskInfoBox = styled(Box)(({ theme }) => {
    return {
        display: 'grid',
        gridTemplateColumns: '5.625rem 1fr',
        gridGap: theme.spacing(1, 0),
        width: '100%',
    };
});

const CustomFormLabel = styled(FormLabel)(() => {
    return {
        alignSelf: 'center',
    };
});

const CustomSelect = styled(Select)(() => {
    return {
        minWidth: 0,
        maxWidth: 'fit-content',
        '& fieldset': {
            border: 'none',
        },
    };
});

const CustomDatePicker = styled(DatePicker)(() => {
    return {
        '& fieldset': {
            border: 'none',
        },
    };
});

const CustomRichTextArea = styled(RichTextArea)(() => {
    return {
        '& .MuiRichTextArea-root': {
            maxHeight: '12rem',
        },
    };
});

export default TemplateTaskView;
